import { Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
  DOCUMENT
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, pairwise } from 'rxjs/operators';
import { PageTemplate } from './models';
import { AuthService, FeedbackFlowService, PageLoadService, PopupService, PwaService, Session, ToastService, UtilityService } from './services';
import { TutorInitService } from './services/tutor-init.service';
import * as constants from './constants';
import { DayjsService } from './services/dayjs.service';
import { environment } from '../environments/environment';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit {
  title = '';
  @ViewChild('messageInsert', { read: ViewContainerRef })
  messageInsert: ViewContainerRef;

  currentLang = '';
  currentPath = '';

  // Make environment available in template
  environment = environment;
  wsData$: Observable<any>;
  lastPageTemplate: PageTemplate;

  constructor(
    public pageLoadService: PageLoadService,
    private router: Router,
    public toastService: ToastService,
    public translate: TranslateService,
    @Inject(DOCUMENT) private document: Document,
    private dayjsService: DayjsService,
    private location: Location,
    public utilityService: UtilityService,
    private authService: AuthService,
    private feedbackFlowService: FeedbackFlowService,
    private cdr: ChangeDetectorRef,
    private pwaService: PwaService,
    private tutorInitService: TutorInitService,
    private popupService: PopupService,
  ) {
    this.currentLang = this.utilityService.country;

    translate.setDefaultLang(this.currentLang);
    translate.use(this.currentLang);
    this.title = constants.Brand[this.currentLang].name;

    this.router.events.subscribe((val) => {
      if (val instanceof NavigationStart) {
        const url = val['url'];
        this.utilityService.trackEvent('virtualPageView', url);
        if (!url.includes('/offline') && this.utilityService.isBrowser() && this.pwaService.isOnline) {
          this.pageLoadService.initPageTemplate(url);
        }
      }
    });
    if (!this.utilityService.isBrowser()) {
      this.pageLoadService.initPageTemplate(this.location.path());
    }
    this.dayjsService.initDayjs();
  }

  ngOnInit(): void {
    this.document.documentElement.lang = this.currentLang;
    this.currentPath = this.location.path();
    this.pwaService.initPwa();
    this.pwaService.startOfflineRouting();
    this.reloadPageTemplateWhenBackOnline();
    // this trackEvent call is required to init GTM
    // without it we're unable to use non-code GTM events for instance
    // including the first pageview as the event trackEvent('virtualPageView', url)
    // above doesn't capture the first page load 
    this.utilityService.trackEvent('event', 'GTM init');
    this.utilityService.onFullScreenChange.subscribe(() => this.cdr.detectChanges());

    this.checkToTriggerDialog();

    this.utilityService.onFooterVisibilityChange.subscribe((isVisible) => {
      this.cdr.detectChanges();
    })
  }

  /**
   * A NavigationStart that happened while the app believed it was offline (a stale browser hint
   * at page load, or a real outage) skipped initPageTemplate. Once the server probe says we are
   * back, load the template for the current URL. PageLoadService ignores the call when that URL
   * is already loaded, so this never fetches twice.
   */
  private reloadPageTemplateWhenBackOnline() {
    if (!this.utilityService.isBrowser()) {
      return;
    }
    this.pwaService.online$
      .pipe(
        distinctUntilChanged(),
        pairwise(),
        filter(([wasOnline, isOnline]) => !wasOnline && isOnline),
      )
      .subscribe(() => {
        const path = this.location.path();
        if (!path.includes('/offline')) {
          this.pageLoadService.initPageTemplate(path);
        }
      });
  }

  /**
   * Runs the login-time checks exactly once per signed-in user, regardless of how the
   * session became available (sessionStorage restore, login, token refresh on a cold tab).
   * sessionChange$ replays and only emits on user switches, so token refreshes and
   * profile edits do not re-trigger the dialogs.
   */
  private checkToTriggerDialog() {
    if (!this.utilityService.isBrowser()) {
      return;
    }
    this.authService.sessionChange$
      .pipe(filter((session): session is Session => !!session))
      .subscribe(({ details }) => {
        this.feedbackFlowService.checkAndOpenPendingDialogs();
        this.tutorInitService.runChecks();
        if (this.authService.isStudent()) {
          this.popupService.checkToOpenSchoolYearVerificationDialog(details);
        }
      });
  }

  ngAfterViewInit(): void {
    this.toastService.viewContainerRef = this.messageInsert;
  }

  pageLoadAction(pageTemplate: PageTemplate) {
    if (pageTemplate && this.lastPageTemplate != pageTemplate) {
      this.lastPageTemplate = pageTemplate;
      this.pageLoadService.setPage(pageTemplate);
      // } else if (!pageTemplate) {
      // console.log('app.component pageLoadAction', pageTemplate);
    }
    return pageTemplate;
  }
}
